<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && supplier.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="supplier"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="supplier"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import utc from 'dayjs/plugin/utc';
import objectSupport from 'dayjs/plugin/objectSupport';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required, email } from 'vuelidate/lib/validators';
import AddEditForm from '@/components/suppliers/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Supplier'
    },

    validations: {
        supplier: {
            name: {
                required
            },
            email: {
                required,
                email
            },
            secondEmail: {
                required,
                email
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit supplier' : 'Add supplier',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Suppliers',
                    to: '/Suppliers'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            supplier: {
                emailTime: '00:00:00'
            },
            serverErrors: []
        };
    },

    computed: {
        sections() {
            const sections = {};

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        }
    },

    async created() {
        if (this.editMode) {
            try {
                const supplier = await this.getSupplier(this.id);

                const time = this.getEmailTime(supplier.emailTime, 'tz');

                supplier.emailTime = time;

                this.supplier = supplier;
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addSupplier: 'suppliers/store',
            deleteSupplier: 'suppliers/destroy',
            getSupplier: 'suppliers/show',
            updateSupplier: 'suppliers/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const inputTime = this.supplier.emailTime;

            const emailTime = this.getEmailTime(this.supplier.emailTime, 'utc');

            const supplier = { ...this.supplier, emailTime, inputTime };

            try {
                if (!this.editMode) {
                    const added = await this.addSupplier(supplier);

                    this.$router.push(`/suppliers/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateSupplier({
                        id: this.id,
                        data: supplier
                    });

                    this.supplier.updatedAt = updatedAt;

                    this.$toaster('Supplier updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteSupplier(this.id);

                this.$router.push('/suppliers');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        getEmailTime(emailTime, option) {
            this.$dayjs.extend(utc);
            this.$dayjs.extend(objectSupport);

            const [hourValue, minuteValue] = emailTime.split(':').map(Number);

            const time = this.$dayjs({
                hour: hourValue,
                minute: minuteValue
            });

            if (option === 'tz') {
                const offset = new Date().getTimezoneOffset();

                return time.subtract(offset, 'minute').format('HH:mm:00');
            }

            if (option === 'utc') {
                return time.utc().format('HH:mm:00');
            }
        }
    }
};
</script>
